import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileDown, Calendar, Search, Filter, BookOpen } from 'lucide-react';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { usePageMetadata } from '../../hooks/usePageMetadata';

import { PageHero } from '../../components/common/PageHero';

interface NewsletterItem {
    id: string;
    title: string;
    description?: string;
    fileUrl: string;
    publishedDate: string;
    orderIndex: number;
}

export const NewsletterArchive: React.FC = () => {
    usePageMetadata('newsletters');

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedYear, setSelectedYear] = useState<string>('all');

    const { data: newsletters = [], isLoading } = useQuery<NewsletterItem[]>({
        queryKey: ['publicNewsletters'],
        queryFn: async () => {
            const response = await apiClient.get('/public/newsletters');
            return response.data.data || [];
        }
    });

    // Extract unique years
    const availableYears = useMemo(() => {
        const years = new Set<string>();
        newsletters.forEach(n => {
            if (n.publishedDate) {
                const y = new Date(n.publishedDate).getFullYear().toString();
                if (y && !isNaN(Number(y))) years.add(y);
            }
        });
        return Array.from(years).sort((a, b) => b.localeCompare(a));
    }, [newsletters]);

    // Filter newsletters
    const filteredNewsletters = useMemo(() => {
        return newsletters.filter(n => {
            const matchesSearch = !searchTerm || 
                n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (n.description && n.description.toLowerCase().includes(searchTerm.toLowerCase()));
            
            const matchesYear = selectedYear === 'all' || 
                (n.publishedDate && new Date(n.publishedDate).getFullYear().toString() === selectedYear);

            return matchesSearch && matchesYear;
        });
    }, [newsletters, searchTerm, selectedYear]);

    // Group filtered newsletters by Year
    const groupedByYear = useMemo(() => {
        const groups: Record<string, NewsletterItem[]> = {};
        filteredNewsletters.forEach(n => {
            const y = n.publishedDate ? new Date(n.publishedDate).getFullYear().toString() : 'Sonstige';
            if (!groups[y]) groups[y] = [];
            groups[y].push(n);
        });
        return groups;
    }, [filteredNewsletters]);

    const sortedGroupYears = useMemo(() => {
        return Object.keys(groupedByYear).sort((a, b) => b.localeCompare(a));
    }, [groupedByYear]);

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <PageHero
                title="Newsletter-Archiv"
                subtitle="Laden Sie alle bisherigen Ausgaben unserer Halbjahres- und Sonder-Newsletter herunter und erfahren Sie mehr über vergangene Aktivitäten, Vereinserfolge und Entwicklungen."
                breadcrumb={[
                    { label: 'Startseite', to: '/' },
                    { label: 'Newsletter-Archiv' }
                ]}
                badge={
                    <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-md border border-white/20">
                        <BookOpen className="h-3.5 w-3.5" /> Mosaik e.V. Publikationen
                    </span>
                }
            />

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24 w-full">

                {/* Search & Filter Controls */}
                <div className="mb-12 bg-secondary/60 rounded-2xl p-4 sm:p-6 border border-border flex flex-col md:flex-row gap-4 items-center justify-between">
                    {/* Search */}
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Newsletter suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                        />
                    </div>

                    {/* Year Filter Buttons */}
                    <div className="flex items-center gap-2 flex-wrap w-full md:w-auto justify-start md:justify-end">
                        <span className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-1 mr-1">
                            <Filter className="h-3.5 w-3.5" /> Jahr:
                        </span>
                        <button
                            onClick={() => setSelectedYear('all')}
                            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                selectedYear === 'all'
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'bg-background hover:bg-secondary text-foreground border border-border'
                            }`}
                        >
                            Alle ({newsletters.length})
                        </button>
                        {availableYears.map(year => (
                            <button
                                key={year}
                                onClick={() => setSelectedYear(year)}
                                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                    selectedYear === year
                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                        : 'bg-background hover:bg-secondary text-foreground border border-border'
                                }`}
                            >
                                {year}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Area */}
                {isLoading ? (
                    <div className="flex justify-center items-center py-24">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
                    </div>
                ) : filteredNewsletters.length === 0 ? (
                    <div className="text-center py-20 bg-secondary/30 rounded-3xl border border-dashed border-border">
                        <FileDown className="mx-auto h-12 w-12 text-muted-foreground/60 mb-3" />
                        <h3 className="text-lg font-semibold text-foreground">Keine Newsletter gefunden</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Es wurden keine Einträge für Ihre aktuellen Filterkriterien gefunden.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {sortedGroupYears.map(year => (
                            <div key={year} className="relative">
                                {/* Year Header */}
                                <div className="flex items-center gap-4 mb-6">
                                    <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                                        <Calendar className="h-6 w-6 text-primary" />
                                        {year}
                                    </h2>
                                    <div className="h-px flex-1 bg-border" />
                                    <span className="text-xs font-semibold text-muted-foreground bg-secondary px-3 py-1 rounded-full">
                                        {groupedByYear[year].length} Ausgabe(n)
                                    </span>
                                </div>

                                {/* Grid of Newsletters in this Year */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {groupedByYear[year].map(newsletter => {
                                        const formattedDate = newsletter.publishedDate
                                            ? new Date(newsletter.publishedDate).toLocaleDateString('de-DE', {
                                                year: 'numeric',
                                                month: 'long'
                                            })
                                            : '';

                                        return (
                                            <div
                                                key={newsletter.id}
                                                className="group relative flex flex-col justify-between rounded-2xl bg-card border border-border p-6 shadow-sm hover:shadow-md hover:border-primary/40 transition-all"
                                            >
                                                <div>
                                                    <div className="flex items-center justify-between mb-4">
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-primary/10 text-primary">
                                                            PDF DOKUMENT
                                                        </span>
                                                        {formattedDate && (
                                                            <span className="text-xs text-muted-foreground">
                                                                {formattedDate}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors mb-2">
                                                        {newsletter.title}
                                                    </h3>

                                                    {newsletter.description && (
                                                        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-6">
                                                            {newsletter.description}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                                                    <a
                                                        href={resolveAssetUrl(newsletter.fileUrl)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors"
                                                    >
                                                        <FileDown className="h-4 w-4" />
                                                        Herunterladen (PDF)
                                                    </a>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
        </div>
    );
};

export default NewsletterArchive;
